const STORAGE_PREFIX = 'workflow-execution-submission:v1';
const inMemorySubmissions = new Map();

function getStorage(storage) {
  if (storage !== undefined) return storage;
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch (_error) {
    return null;
  }
}

function getStorageKey(batchId, requestType, pipeline) {
  return [
    STORAGE_PREFIX,
    encodeURIComponent(String(batchId)),
    encodeURIComponent(String(requestType)),
    ...(pipeline ? [encodeURIComponent(String(pipeline))] : []),
  ].join(':');
}

function cloneJsonValue(value) {
  return JSON.parse(JSON.stringify(value));
}

function canonicalizeJsonValue(value) {
  if (Array.isArray(value)) {
    return value.map(canonicalizeJsonValue);
  }
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        if (value[key] !== undefined) {
          result[key] = canonicalizeJsonValue(value[key]);
        }
        return result;
      }, {});
  }
  return value;
}

function readRetainedSubmission(storageKey, storage) {
  const inMemory = inMemorySubmissions.get(storageKey);
  if (inMemory) return inMemory;
  if (!storage) return null;

  try {
    const serialized = storage.getItem(storageKey);
    if (!serialized) return null;
    const parsed = JSON.parse(serialized);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !parsed.payloadFingerprint ||
      !parsed.clientRequestId ||
      !parsed.payload
    ) {
      storage.removeItem(storageKey);
      return null;
    }
    inMemorySubmissions.set(storageKey, parsed);
    return parsed;
  } catch (_error) {
    return null;
  }
}

function writeRetainedSubmission(storageKey, submission, storage) {
  inMemorySubmissions.set(storageKey, submission);
  if (!storage) return;
  try {
    storage.setItem(storageKey, JSON.stringify(submission));
  } catch (_error) {
    // The in-memory copy still protects retries during this page lifetime.
  }
}

export function retainExecutionRequestSubmission({
  batchId,
  requestType,
  pipeline,
  payload,
  createClientRequestId,
  storage,
}) {
  const resolvedStorage = getStorage(storage);
  const storageKey = getStorageKey(batchId, requestType, pipeline);
  const payloadWithoutIdentity = cloneJsonValue(payload);
  delete payloadWithoutIdentity.client_request_id;
  const payloadFingerprint = JSON.stringify(
    canonicalizeJsonValue(payloadWithoutIdentity)
  );
  const retained = readRetainedSubmission(storageKey, resolvedStorage);

  if (retained && (pipeline || retained.payloadFingerprint === payloadFingerprint)) {
    return {
      clientRequestId: retained.clientRequestId,
      payload: cloneJsonValue(retained.payload),
      reused: true,
    };
  }

  const generatedClientRequestId = createClientRequestId();
  const clientRequestId =
    generatedClientRequestId == null
      ? ''
      : String(generatedClientRequestId).trim();
  if (!clientRequestId) {
    throw new Error('A client request ID is required for execution submission.');
  }
  const exactPayload = {
    ...payloadWithoutIdentity,
    client_request_id: clientRequestId,
  };
  const submission = {
    payloadFingerprint,
    clientRequestId,
    payload: exactPayload,
  };
  // Initial admission must survive a page refresh before any HTTP request is sent.
  if (pipeline) {
    if (!resolvedStorage) throw new Error('Session storage is required for safe New Run recovery.');
    resolvedStorage.setItem(storageKey, JSON.stringify(submission));
    if (resolvedStorage.getItem(storageKey) !== JSON.stringify(submission)) {
      throw new Error('Could not persist the New Run request. No request was sent.');
    }
  }
  writeRetainedSubmission(storageKey, submission, resolvedStorage);

  return {
    clientRequestId,
    payload: cloneJsonValue(exactPayload),
    reused: false,
  };
}

export function clearRetainedExecutionRequestSubmission({
  batchId,
  requestType,
  pipeline,
  storage,
}) {
  const resolvedStorage = getStorage(storage);
  const storageKey = getStorageKey(batchId, requestType, pipeline);
  inMemorySubmissions.delete(storageKey);
  if (!resolvedStorage) return;
  try {
    resolvedStorage.removeItem(storageKey);
  } catch (_error) {
    // The in-memory copy has already been cleared.
  }
}


export function getRetainedExecutionRequestSubmission({ batchId, requestType, pipeline, storage }) {
  const resolvedStorage = getStorage(storage);
  const retained = readRetainedSubmission(getStorageKey(batchId, requestType, pipeline), resolvedStorage);
  if (retained) return cloneJsonValue(retained);
  // Never silently abandon a pre-cycle, Batch-wide unresolved request.
  const legacy = pipeline && readRetainedSubmission(getStorageKey(batchId, requestType), resolvedStorage);
  return legacy ? { ...cloneJsonValue(legacy), legacyScope: true } : null;
}

// Durable request IDs are kept separately from unresolved submissions, so opening
// another pipeline or closing another cycle cannot replace an earlier tracker.
export function readAcceptedInitialRuns(batchId) {
  try {
    const entries = JSON.parse(getStorage()?.getItem(`accepted-initial-runs:v1:${batchId}`) || '[]');
    return Array.isArray(entries) ? entries.filter((entry) => entry?.response?.execution_request_id && ['size', 'encapsulation'].includes(entry.pipeline)) : [];
  } catch (_) { return []; }
}

export function recordAcceptedInitialRun(batchId, entry) {
  const entries = readAcceptedInitialRuns(batchId);
  const next = [...entries.filter((item) => item.response.execution_request_id !== entry.response.execution_request_id), entry];
  const storage = getStorage();
  if (!storage) throw new Error('Cannot persist accepted execution identity.');
  storage.setItem(`accepted-initial-runs:v1:${batchId}`, JSON.stringify(next));
  return next;
}
